/**
 * Unified Firebase RTDB CRUD for all content under `contents`.
 */

import {
  ref,
  push,
  set,
  get,
  remove,
  update,
  query,
  orderByChild,
  equalTo,
  limitToLast,
} from "firebase/database";
import { db } from "../../auth/firebase";
import logger from "../../utils/logger";
import {
  CONTENT_PREVIEW_LENGTH,
  CONTENT_STATUS,
  CONTENT_VISIBILITY,
  normalizeContentType,
} from "./contentConstants";
import { estimateReadingMinutes, normalizeTags, stripHtml } from "../../utils/contentSharedUtils";
import { contentBlob, findBlockedKeyword } from "../../security/abuseKeywords";
import { getPublicSiteConfig } from "../../services/platformConfigService";

async function assertContentAllowed(data) {
  const site = await getPublicSiteConfig();
  const hit = findBlockedKeyword(contentBlob(data), site.blockedKeywords);
  if (hit) {
    throw new Error("This piece includes a blocked term and was not saved.");
  }
}

export const CONTENTS_DB_PATH = "contents";

const DEFAULT_PAGE_SIZE = 20;

const buildContentPreview = (contentHtml, maxLen = CONTENT_PREVIEW_LENGTH) => {
  const text = stripHtml(contentHtml || "");
  if (!text) return "";
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen).trim()}...`;
};

const normalizeStatusAndVisibility = (statusValue, visibilityValue) => {
  const status = statusValue === CONTENT_STATUS.PUBLISHED ? CONTENT_STATUS.PUBLISHED : CONTENT_STATUS.DRAFT;
  const rawVisibility = [CONTENT_VISIBILITY.PUBLIC, CONTENT_VISIBILITY.PRIVATE, CONTENT_VISIBILITY.PREMIUM].includes(
    visibilityValue
  )
    ? visibilityValue
    : CONTENT_VISIBILITY.PUBLIC;
  const visibility = status === CONTENT_STATUS.PUBLISHED ? rawVisibility : CONTENT_VISIBILITY.PRIVATE;
  return { status, visibility };
};

const sanitizePayload = (data) => {
  const tags = normalizeTags(data.tags || []);
  const { status, visibility } = normalizeStatusAndVisibility(data.status, data.visibility);

  return {
    title: String(data.title || "").trim(),
    subtitle: data.subtitle != null ? String(data.subtitle) : "",
    content: data.content != null ? String(data.content) : "",
    type: data.type,
    tags,
    coverImage: data.coverImage != null ? String(data.coverImage).trim() : "",
    language: data.language != null ? String(data.language).trim() : "en",
    status,
    visibility,
    allowComments: data.allowComments !== false,
    isFeatured: !!data.isFeatured,
    isBoosted: !!data.isBoosted,
    metadata: data.metadata && typeof data.metadata === "object" ? data.metadata : {},
    isPremium: visibility === CONTENT_VISIBILITY.PREMIUM || !!data.isPremium,
  };
};

export const createContent = async (rawData, creatorInfo = null) => {
  try {
    const data = sanitizePayload(rawData);
    await assertContentAllowed(data);
    const rootRef = ref(db, CONTENTS_DB_PATH);
    const newRef = push(rootRef);
    const now = new Date().toISOString();
    const contentPreview = buildContentPreview(data.content);
    const readingTimeMinutes = estimateReadingMinutes(data.content);

    const record = {
      ...data,
      id: newRef.key,
      authorId: creatorInfo?.uid || null,
      createdAt: now,
      updatedAt: now,
      likes: {},
      comments: {},
      contentPreview,
      readingTimeMinutes,
      views: 0,
      reads: 0,
      completions: 0,
      isBoosted: !!data.isBoosted,
      boostedAt: data.isBoosted ? Date.now() : null,
    };

    if (creatorInfo) {
      record.createdBy = creatorInfo.uid;
      record.createdByName = creatorInfo.displayName || creatorInfo.authorName || "Writer";
      if (creatorInfo.authorName) record.authorName = creatorInfo.authorName;
    }

    await set(newRef, record);
    return newRef.key;
  } catch (error) {
    logger.error(error, "contentCrudService - createContent");
    throw error;
  }
};

export const getContentById = async (id) => {
  const snapshot = await get(ref(db, `${CONTENTS_DB_PATH}/${id}`));
  if (!snapshot.exists()) throw new Error("Content not found");
  const val = snapshot.val();
  if (!val.likes) val.likes = {};
  if (!val.comments) val.comments = {};
  return val;
};

const sortNewestFirst = (arr) =>
  [...arr].sort(
    (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
  );

const isPublicReadableRow = (row) => {
  const status = String(row?.status || "").toLowerCase();
  const visibility = String(row?.visibility || CONTENT_VISIBILITY.PUBLIC).toLowerCase();
  if (status === CONTENT_STATUS.DRAFT) return false;
  if (visibility === CONTENT_VISIBILITY.PRIVATE || visibility === CONTENT_VISIBILITY.PREMIUM) return false;
  return visibility === CONTENT_VISIBILITY.PUBLIC;
};

const rowsFromSnapshot = (snapshot) => {
  const rows = [];
  if (!snapshot?.exists()) return rows;
  snapshot.forEach((child) => {
    const row = child.val();
    if (!row) return;
    if (!row.id) row.id = child.key;
    if (!row.likes) row.likes = {};
    if (!row.comments) row.comments = {};
    rows.push(row);
  });
  return rows;
};

/** Published public works only. Matches RTDB query rules (`visibility == public`). */
export const getPublicCatalog = async (max = 200) => {
  const safeMax = Math.max(1, Math.min(Number(max) || 200, 500));
  const q = query(
    ref(db, CONTENTS_DB_PATH),
    orderByChild("visibility"),
    equalTo(CONTENT_VISIBILITY.PUBLIC),
    limitToLast(safeMax)
  );
  const snapshot = await get(q);
  return sortNewestFirst(rowsFromSnapshot(snapshot).filter(isPublicReadableRow));
};

export const getOwnedContents = async (uid) => {
  if (!uid) return [];
  const map = new Map();
  const pull = async (field) => {
    const q = query(ref(db, CONTENTS_DB_PATH), orderByChild(field), equalTo(uid));
    const snap = await get(q);
    rowsFromSnapshot(snap).forEach((row) => {
      if (row.id) map.set(row.id, row);
    });
  };
  await pull("createdBy");
  await pull("authorId");
  return sortNewestFirst([...map.values()]);
};

/** Admin catalog. Requires an admin `orderByChild(createdAt)` read rule. */
export const getAdminContents = async () => {
  const q = query(ref(db, CONTENTS_DB_PATH), orderByChild("createdAt"));
  const snapshot = await get(q);
  return sortNewestFirst(rowsFromSnapshot(snapshot));
};

/**
 * @param {{ scope?: "public"|"writer"|"admin", uid?: string }} [options]
 */
export const getAllContentsLegacy = async (options = {}) => {
  const scope = options?.scope || "public";
  if (scope === "admin") return getAdminContents();
  if (scope === "writer" && options?.uid) {
    const [owned, pub] = await Promise.all([getOwnedContents(options.uid), getPublicCatalog(200)]);
    const map = new Map();
    [...pub, ...owned].forEach((row) => {
      if (row?.id) map.set(row.id, row);
    });
    return sortNewestFirst([...map.values()]);
  }
  return getPublicCatalog(200);
};

/**
 * Paginated public catalog (newest first), sliced in memory from the public visibility query.
 * Cursors use content id. Private, premium, and draft rows are not included.
 */
export const getAllContents = async (options = {}) => {
  const { limit = DEFAULT_PAGE_SIZE, lastKey = null, type = null } = options;
  const safeLimit = Math.max(1, Math.min(Number(limit) || DEFAULT_PAGE_SIZE, 100));

  try {
    const all = await getPublicCatalog(200);
    const canonicalType = type != null ? normalizeContentType(type) : null;
    const filtered = canonicalType
      ? all.filter((r) => normalizeContentType(r.type) === canonicalType)
      : all;
    let startIndex = 0;
    if (lastKey) {
      const ix = filtered.findIndex((r) => r.id === lastKey);
      if (ix !== -1) startIndex = ix + 1;
    }
    const page = filtered.slice(startIndex, startIndex + safeLimit);
    const tail = page.length ? page[page.length - 1] : null;
    return {
      items: page,
      hasMore: startIndex + safeLimit < filtered.length,
      endCursorCreatedAt: tail?.createdAt ?? null,
      endCursorId: tail?.id ?? null,
    };
  } catch (error) {
    logger.error(error, "contentCrudService - getAllContents");
    throw error;
  }
};

/**
 * All rows whose canonical {@link normalizeContentType} matches `type`
 * (legacy `poem` / `book` / etc. match `story` the same as stored `story`).
 */
export const getContentsByType = async (type) => {
  const canonical = normalizeContentType(type);
  const all = await getPublicCatalog(200);
  return all.filter((r) => normalizeContentType(r.type) === canonical);
};

export const getLatestContentsByType = async (type, limit = 3) => {
  const safeLimit = Math.max(1, Number(limit) || 1);
  const rows = await getContentsByType(type);
  return rows.slice(0, safeLimit);
};

/**
 * Shallow-merge `metadata` with existing content (for moderation / SEO fields).
 * @param {string} id
 * @param {Record<string, unknown>} partialMetadata
 */
export const mergeContentMetadata = async (id, partialMetadata) => {
  const current = await getContentById(id);
  const metadata = { ...(current.metadata || {}), ...(partialMetadata || {}) };
  await update(ref(db, `${CONTENTS_DB_PATH}/${id}`), {
    metadata,
    updatedAt: new Date().toISOString(),
  });
};

/**
 * Apply moderation outcome on content + optional unpublish when rejecting.
 * @param {string} contentId
 * @param {'approved'|'rejected'} decision
 * @param {{ moderatorUid?: string, note?: string }} meta
 */
export const applyContentModeration = async (contentId, decision, meta = {}) => {
  const current = await getContentById(contentId);
  const moderationStatus = decision === "approved" ? "approved" : "rejected";
  const nextMeta = {
    ...(current.metadata || {}),
    moderationStatus,
    moderatedAt: new Date().toISOString(),
    moderatedBy: meta.moderatorUid || "",
    moderationNote: meta.note != null ? String(meta.note).slice(0, 2000) : "",
  };
  if (decision === "rejected") {
    const { status, visibility } = normalizeStatusAndVisibility(CONTENT_STATUS.DRAFT, CONTENT_VISIBILITY.PRIVATE);
    await update(ref(db, `${CONTENTS_DB_PATH}/${contentId}`), {
      status,
      visibility,
      metadata: nextMeta,
      updatedAt: new Date().toISOString(),
    });
  } else {
    await update(ref(db, `${CONTENTS_DB_PATH}/${contentId}`), {
      metadata: nextMeta,
      updatedAt: new Date().toISOString(),
    });
  }
};

export const updateContent = async (id, updates) => {
  await assertContentAllowed(updates);
  const next = { ...updates };
  if (typeof updates.status !== "undefined" || typeof updates.visibility !== "undefined") {
    const current = await getContentById(id);
    const { status, visibility } = normalizeStatusAndVisibility(
      typeof updates.status !== "undefined" ? updates.status : current?.status,
      typeof updates.visibility !== "undefined" ? updates.visibility : current?.visibility
    );
    next.status = status;
    next.visibility = visibility;
  }
  if (typeof updates.content !== "undefined") {
    next.contentPreview = buildContentPreview(updates.content);
    next.readingTimeMinutes = estimateReadingMinutes(updates.content);
  }
  if (updates.tags) next.tags = normalizeTags(updates.tags);
  if (next.visibility != null) {
    next.isPremium =
      next.visibility === CONTENT_VISIBILITY.PREMIUM || updates.isPremium === true;
  }
  next.updatedAt = new Date().toISOString();
  await update(ref(db, `${CONTENTS_DB_PATH}/${id}`), next);
};

export const deleteContent = async (id) => {
  await remove(ref(db, `${CONTENTS_DB_PATH}/${id}`));
};

/**
 * All unified `contents` nodes owned by this user (matches createdBy or authorId).
 * @param {string} uid
 * @returns {Promise<string[]>}
 */
export const getUserContentIds = async (uid) => {
  if (!uid) return [];
  const ids = new Set();
  const addFromQuery = async (field) => {
    const q = query(ref(db, CONTENTS_DB_PATH), orderByChild(field), equalTo(uid));
    const snap = await get(q);
    if (!snap.exists()) return;
    snap.forEach((child) => {
      if (child.key) ids.add(child.key);
    });
  };
  await addFromQuery("createdBy");
  await addFromQuery("authorId");
  return [...ids];
};

export const getUserContentCount = async (uid) => (await getUserContentIds(uid)).length;

export const deleteAllContentsForUser = async (uid) => {
  const ids = await getUserContentIds(uid);
  await Promise.all(ids.map((id) => remove(ref(db, `${CONTENTS_DB_PATH}/${id}`))));
};

export const addLike = async (contentId, userId) => {
  if (!userId) throw new Error("Sign in to like this piece.");
  await set(ref(db, `${CONTENTS_DB_PATH}/${contentId}/likes/${userId}`), true);
};

export const removeLike = async (contentId, userId) => {
  if (!userId) throw new Error("Sign in to remove this like.");
  await remove(ref(db, `${CONTENTS_DB_PATH}/${contentId}/likes/${userId}`));
};

export const deleteComment = async (contentId, commentId, userId) => {
  if (!contentId || !commentId || !userId) throw new Error("Could not delete that comment.");
  const commentRef = ref(db, `${CONTENTS_DB_PATH}/${contentId}/comments/${commentId}`);
  const snap = await get(commentRef);
  if (!snap.exists()) return;
  if (snap.val()?.userId !== userId) throw new Error("You can delete only your own comment.");
  await remove(commentRef);
};

export const addComment = async (contentId, userName, commentText, userId) => {
  if (!userId) {
    throw new Error("You need to be signed in to comment.");
  }
  const commentsRef = ref(db, `${CONTENTS_DB_PATH}/${contentId}/comments`);
  const newRef = push(commentsRef);
  await set(newRef, {
    userId,
    userName,
    text: commentText,
    timestamp: Date.now(),
  });
  return newRef.key;
};
